import PropTypes from 'prop-types';

/*
utils to handle common logic related to adding spacing between/around components via PropTypes
*/
const sizePropTypes = PropTypes.oneOf(['none', 'xs', 'sm', 'smd', 'md', 'mlg', 'lg', 'xl']);
function generateSpacingPropTypes({
  denylist = []
} = {}) {
  const shape = {
    all: sizePropTypes,
    horizontal: sizePropTypes,
    vertical: sizePropTypes,
    top: sizePropTypes,
    right: sizePropTypes,
    bottom: sizePropTypes,
    left: sizePropTypes,
    default: sizePropTypes
  };
  denylist.forEach(k => {
    delete shape[k];
  });
  return PropTypes.shape(shape);
}
const marginPropTypes = generateSpacingPropTypes();
const verticalMarginPropTypes = generateSpacingPropTypes({
  denylist: ['all', 'horizontal', 'left', 'right']
});
const paddingPropTypes = generateSpacingPropTypes();
const verticalPaddingPropTypes = generateSpacingPropTypes({
  denylist: ['all', 'horizontal', 'left', 'right']
});

/**
 * Used when spacing prop includes a "default" property
 * Given padding or margin settings
 * adds a default value to any sides where an explicit value was not provided
 *
 * @param {object} settings
 *
 * @returns {object} newSettings
 */
function addDefaultSpacing(settings, value = 'none') {
  const newSettings = {
    ...settings
  };
  const keys = Object.keys(settings);
  if (!settings || keys.length === 0) return {
    all: value
  };
  if (keys.includes('all')) return settings;
  const total = {
    top: 1,
    left: 1,
    right: 1,
    bottom: 1
  };
  keys.forEach(currentValue => {
    total[currentValue] -= 1;
    if (currentValue === 'horizontal') {
      total.left -= 1;
      total.right -= 1;
    } else if (currentValue === 'vertical') {
      total.top -= 1;
      total.bottom -= 1;
    }
  });
  Object.keys(total).forEach(key => {
    if (total[key] === 1) {
      newSettings[key] = value;
    }
  });
  return newSettings;
}

/**
 * Given the spacing props, returns an array of CSS classes to apply the padding/margin
 * @param {string} prefix The prefix for the CSS class
 * @param {object} settings The margin or padding props
 * @returns {array} array of CSS classes
 */
function getSpacing(prefix, settings) {
  let finalSettings = {
    ...settings
  };
  if (!finalSettings || !prefix) return null;
  if (finalSettings.default) {
    delete finalSettings.default;
    finalSettings = addDefaultSpacing(finalSettings, settings.default);
  }
  const settingsArray = Object.keys(finalSettings);
  if (settingsArray.length === 0) return null;
  const sidemap = {
    top: 'top',
    left: 'left',
    right: 'right',
    bottom: 'bot',
    horizontal: 'hor',
    vertical: 'ver'
  };
  return settingsArray.map(side => {
    if (side === 'all') return `${prefix}-${finalSettings[side]}`;
    return `${prefix}-${sidemap[side]}-${finalSettings[side]}`;
  });
}

/*
Given margn PropTypes, generates the CSS classes to use
*/
function getMargin(margin) {
  return getSpacing('st_m', margin);
}
function getPadding(padding) {
  return getSpacing('st_p', padding);
}

/**
 * Given an object with props from user, returns a new object that removes
 * all properties that are disallowed
 *
 * @param {object} settings the padding/margin props provided to component
 * @param {array} denylist array of strings with name of disallowed props
 */
function sanitizeProps(settings, denylist = []) {
  const newSettings = {
    ...settings
  };
  Object.keys(settings).forEach(k => {
    if (denylist.includes(k)) delete newSettings[k];
  });
  return newSettings;
}

/*
given array of original css class arrays
and margin PropTypes
return new array with margin classes, or the original aray
*/
function addMarginClasses(margin, cssClasses = [], denylist = []) {
  if (!margin || Object.keys(margin).length === 0) return cssClasses;
  return (cssClasses || []).concat(getMargin(sanitizeProps(margin, denylist) || []));
}

/*
given array of original css class arrays
and padding PropTypes
return new array with margin classes, or the original aray
*/
function addPaddingClasses(padding, cssClasses = [], denylist = []) {
  if (!padding || Object.keys(padding).length === 0) return cssClasses;
  return (cssClasses || []).concat(getPadding(sanitizeProps(padding, denylist)) || []);
}

/*
given margin or padding PropTypes
add css classnames
and return new array of css classes
*/
function addSpacingClasses({
  padding = {},
  margin = {},
  cssClasses = [],
  denylistPadding = [],
  denylistMargin = []
}) {
  let newClasses = cssClasses;
  newClasses = addPaddingClasses(padding, newClasses, denylistPadding);
  newClasses = addMarginClasses(margin, newClasses, denylistMargin);
  return newClasses;
}

var spacing = /*#__PURE__*/Object.freeze({
  __proto__: null,
  addDefaultSpacing: addDefaultSpacing,
  sizePropTypes: sizePropTypes,
  marginPropTypes: marginPropTypes,
  paddingPropTypes: paddingPropTypes,
  getMargin: getMargin,
  getPadding: getPadding,
  addMarginClasses: addMarginClasses,
  addPaddingClasses: addPaddingClasses,
  verticalMarginPropTypes: verticalMarginPropTypes,
  verticalPaddingPropTypes: verticalPaddingPropTypes,
  addSpacingClasses: addSpacingClasses
});

export { addSpacingClasses as a, addMarginClasses as b, sizePropTypes as c, verticalPaddingPropTypes as d, getPadding as g, marginPropTypes as m, paddingPropTypes as p, spacing as s, verticalMarginPropTypes as v };
